package fr.frux.core.common.storage

import fr.frux.core.common.logging.Logger
import kotlinx.browser.window
import org.w3c.dom.Storage

/** Le sous-ensemble de `Storage` dont préférences et session ont besoin, pour pouvoir le remplacer. */
internal interface KeyValueStore {
    fun getItem(key: String): String?

    fun setItem(
        key: String,
        value: String,
    )

    fun removeItem(key: String)
}

internal class BrowserStorage(
    val store: KeyValueStore,
    /** Faux quand [store] est le repli en mémoire : rien ne survit au rechargement. */
    val persistent: Boolean,
)

internal class InMemoryKeyValueStore : KeyValueStore {
    private val entries = mutableMapOf<String, String>()

    override fun getItem(key: String): String? = entries[key]

    override fun setItem(
        key: String,
        value: String,
    ) {
        entries[key] = value
    }

    override fun removeItem(key: String) {
        entries.remove(key)
    }
}

private class LocalStorageKeyValueStore(
    private val storage: Storage,
) : KeyValueStore {
    override fun getItem(key: String): String? = storage.getItem(key)

    override fun setItem(
        key: String,
        value: String,
    ) = storage.setItem(key, value)

    override fun removeItem(key: String) = storage.removeItem(key)
}

private const val PROBE_KEY = "frux_storage_probe"
private const val TAG = "BrowserStorage"

/**
 * Quand le navigateur bloque le stockage du site, la simple lecture de `window.localStorage` lève
 * une SecurityError (#610). Non rattrapée, elle tuait la composition au démarrage et laissait le
 * pré-splash à l'écran pour toujours. On sonde donc une fois, lecture et écriture comprises
 * (certains modes privés exposent l'objet mais refusent `setItem`), puis on replie en mémoire.
 * La SecurityError est une DOMException sans type Kotlin : seul Throwable la rattrape.
 */
@Suppress("TooGenericExceptionCaught")
internal fun resolveBrowserStorage(access: () -> Storage): BrowserStorage =
    try {
        val storage = access()
        storage.setItem(PROBE_KEY, PROBE_KEY)
        storage.removeItem(PROBE_KEY)
        BrowserStorage(LocalStorageKeyValueStore(storage), persistent = true)
    } catch (e: Throwable) {
        Logger.i(TAG, "localStorage refusé par le navigateur, repli en mémoire : ${e.message}")
        BrowserStorage(InMemoryKeyValueStore(), persistent = false)
    }

/** Résolu une seule fois : préférences et session doivent partager le même repli. */
internal val browserStorage: BrowserStorage by lazy { resolveBrowserStorage { window.localStorage } }
